import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listCollections, createCollection } from '@/lib/frames-api';
import { ArrowLeft, Image, Home, Heart, Briefcase, Plus } from 'lucide-react';

const PRESETS = [
  { name: 'Home', icon: Home },
  { name: 'Grandparents', icon: Heart },
  { name: 'Office', icon: Briefcase },
];

export default function FramesPage() {
  const navigate = useNavigate();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';

  const [collections, setCollections] = useState([]);
  const [tray, setTray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customName, setCustomName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try { const r = await listCollections(eventId); setCollections(r.collections || []); setTray(r.tray || []); setError(null); }
    catch (e) { setError(e?.message || 'Failed to load collections.'); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  async function newCollection(name) {
    if (!eventId || !name.trim()) return;
    setBusy(true);
    try {
      const r = await createCollection(eventId, name.trim());
      setCollections(p => [...p, r.collection]);
      setCustomName('');
      navigate(`/frame-collection?event_id=${encodeURIComponent(eventId)}&collection_id=${encodeURIComponent(r.collection.id)}`);
    } catch (e) { window.alert(e?.message || 'Create failed.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="frames-screen">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="frames-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-4">Frame Collections</h1>

      <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="frames-trust-copy">
        <p className="font-bold text-[#0B1B46]">Export-only, host-controlled</p>
        <p className="text-sm text-[#5B6472] mt-1">Only photos you selected can be exported. Frames are packaged as a zip; nothing is pushed to any device.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="frames-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]">{error}</div>
      ) : (
        <div className="flex flex-col gap-4">
          {collections.length === 0 ? (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-sm text-[#5B6472]" data-testid="frames-empty">No collections yet. Create one below.</div>
          ) : (
            collections.map(c => (
              <button key={c.id} onClick={() => navigate(`/frame-collection?event_id=${encodeURIComponent(eventId)}&collection_id=${encodeURIComponent(c.id)}`)}
                className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-left hover:border-[#146BFF] hover:shadow-sm transition-all"
                data-testid={`frames-collection-${c.id}`}>
                <div className="flex items-center gap-3 mb-1">
                  <Image className="w-5 h-5 text-[#146BFF]" />
                  <span className="text-base font-extrabold text-[#0B1B46] flex-1 truncate" data-testid={`frames-collection-name-${c.id}`}>{c.name}</span>
                  <span className="min-w-[28px] h-[22px] bg-[#146BFF] text-white text-xs font-extrabold rounded-full flex items-center justify-center px-1.5">{c.item_count}</span>
                </div>
                {c.description && <p className="text-sm text-[#5B6472]">{c.description}</p>}
                <p className="text-[11px] text-[#5B6472] mt-1">v{c.version} · updated {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '—'}</p>
              </button>
            ))
          )}

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="frames-tray-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-2">Frame-selected views available ({tray.length})</p>
            <p className="text-sm text-[#5B6472]">{tray.length === 0 ? 'Toggle "Add to Frame" on views in the Vault to have them appear here.' : `${tray.length} frame-selected view(s) ready to place into any collection.`}</p>
          </div>

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="frames-new-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Create a collection</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS.map(p => (
                <button key={p.name} onClick={() => newCollection(p.name)} disabled={busy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#146BFF] text-[#146BFF] font-bold text-sm bg-white hover:bg-[#EFF5FF] disabled:opacity-50 transition-colors"
                  data-testid={`frames-preset-${p.name.toLowerCase()}`}>
                  <p.icon className="w-3.5 h-3.5" /> {p.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Custom name…"
                className="flex-1 h-10 px-4 border border-[#E3E8EF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] bg-white"
                data-testid="frames-custom-name-input" />
              <button onClick={() => newCollection(customName)} disabled={!customName.trim() || busy}
                className="h-10 px-4 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg text-sm transition-colors"
                data-testid="frames-create-btn"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
